import { ArrowIcon, LinkIcon } from 'assets/icons';
import { GlassLoader } from 'components/Common/GlassLoader';
import {
  BBBright,
  BBDARK,
  GRAY20,
  GRAY80,
  GRAY90,
  SB,
  SubtitleL,
} from 'global-styles';
import { MainButton } from 'global-styles/MainButton';
import Link from 'next/link';
import { PostType } from 'react-query/blogApi';
import { useRelatedPostList } from 'react-query/blogHooks';
import styled from 'styled-components';
import PostCard from './PostCard';
import linkedInIcon from 'public/asset/blog/share/linkedin.png';
import naverblogIcon from 'public/asset/blog/share/naverblog.png';
import twitterIcon from 'public/asset/blog/share/twitter.png';
import { useRouter } from 'next/router';

interface Props {
  postId: number;
  content: string;
  tags: string[];
  title: string;
  postType: PostType;
}

const PostContentArea = ({ content, tags, postId, title }: Props) => {
  const { locale } = useRouter();
  const { data, isLoading, isFetching } = useRelatedPostList(postId);
  const relatedPosts = data?.posts;
  const contentHTML = { __html: content };
  const handleCopyURL = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => {
        window.alert('URL Copied to the clipboard.');
      })
      .catch(() => {});
  };

  return (
    <PostContentAreaContainer>
      <div className="content-area" dangerouslySetInnerHTML={contentHTML} />
      {/* share link area */}
      <div className="link-area">
        {/* twitter */}
        <a
          href={`https://twitter.com/intent/tweet?url=https://blue-dot.io/ko/blog/post/${postId}&text=${title}`}
          target="_blank"
          rel="noreferrer"
          className="twitter-share-button share-link"
        >
          <span
            className="share-icon"
            style={{ backgroundImage: `url("${twitterIcon.src}")` }}
          />
        </a>

        {/* naver blog */}
        <a
          className="share-link"
          target="_blank"
          rel="noreferrer"
          href={`http://blog.naver.com/openapi/share?url=https://blue-dot.io/ko/blog/post/${postId}&text=${title}`}
        >
          <span
            className="share-icon"
            style={{ backgroundImage: `url("${naverblogIcon.src}")` }}
          />
        </a>

        {/* linked in */}
        <a
          className="share-link"
          href={`https://www.linkedin.com/sharing/share-offsite/?url=$https://blue-dot.io/ko/blog/post/${postId}`}
          target="_blank"
          rel="noreferrer"
        >
          <span
            className="share-icon"
            style={{ backgroundImage: `url("${linkedInIcon.src}")` }}
          />
        </a>

        {/* copy link address */}
        <button className="share-link copy-link" onClick={handleCopyURL}>
          <LinkIcon />
        </button>
      </div>

      {/* tags */}
      {tags && tags.length !== 0 && (
        <ul className="tags-area">
          {tags.map(tag => (
            <Tag key={tag}>
              <span>#</span>
              <span>{tag}</span>
            </Tag>
          ))}
        </ul>
      )}

      <div className="related-post-area">
        {/* 목록으로 */}
        <Link href="/blog/">
          <MainButton
            tag="a"
            buttonType="default"
            text={locale === 'en' ? 'Go to list' : '목록으로'}
            className="ok-btn"
            icon={<ArrowIcon />}
            iconPosition="left"
            injectInlineStyles={{
              width: '147px',
              height: '48px',
            }}
          />
        </Link>

        {/* 연관 포스트 렌더링 */}
        {isLoading || isFetching ? (
          <GlassLoader loadingText="Loading Events..." />
        ) : (
          <>
            {relatedPosts && relatedPosts.length !== 0 && (
              <div className="related-post-wrapper">
                <SubtitleL className="related-post-t">
                  {locale === 'en' ? 'Related posts' : '연관 포스트'}
                </SubtitleL>
                <ul className="related-post-list">
                  {relatedPosts.map(rp => (
                    <PostCard
                      key={rp.id}
                      title={rp.title}
                      category={rp.category}
                      thumbnail={rp.thumbnail}
                      postId={rp.id}
                      forPostUI
                    />
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </PostContentAreaContainer>
  );
};

export default PostContentArea;

const PostContentAreaContainer = styled.section`
  .share-link {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .share-link.copy-link {
    width: 32px;
    height: 32px;
    margin-left: 6px;
  }
  .share-icon {
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
    display: block;
    width: 100%;
    height: 100%;
  }
  .related-post-area {
    padding-top: 32px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .related-post-wrapper {
    width: 100%;
  }
  .related-post-t {
    text-align: center;
    margin: 40px 0;
    color: ${GRAY20};
  }
  .related-post-list {
    width: 100%;
    text-align: left;
    display: flex;
    justify-content: center;
    padding: 4px;
  }
  .related-post-list > li {
    flex-shrink: 0;
  }

  .related-post-list > li + li {
    margin-left: 16px;
  }

  .link-area {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px 0;
    margin-bottom: 16px;
    padding-top: 64px;
  }
  .link-area > * + * {
    margin-left: 12px;
  }

  .tags-area {
    display: flex;
    padding: 8px 0;
    flex-wrap: wrap;
    justify-content: flex-start;
  }
  .content-area {
    border-bottom: 1px solid ${GRAY90};
    padding: 32px 0;
    *,
    *::before,
    *::after {
      font-size: 14px;
      font-family: 'Noto Sans KR', sans-serif;
    }
    h2,
    h3,
    h4,
    h5,
    h6 {
      font-weight: bold;
      padding: 8px 0;
    }
    h2,
    h3,
    h4 {
      color: #111;
      line-height: 120%;
    }
    h2 {
      font-size: 24px;
      line-height: 40px;
    }
    h3 {
      font-size: 19px;
      line-height: 29px;
    }
    h4 {
      font-size: 16px;
      line-height: 26px;
    }
    h5 {
      font-size: 13px;
      line-height: 21px;
    }
    h6 {
      font-size: 10px;
      line-height: 17px;
    }

    ul {
      padding-left: 16px;
      list-style: disc;
    }
    ul ul {
      list-style: circle;
      margin: 0;
    }
    ul ul ul {
      margin: 0;
      list-style: square;
    }
    ol {
      padding-left: 16px;
      list-style: number;
    }
    ol ol,
    ol ol ol {
      margin: 0;
    }
    ul,
    ol {
      margin: 8px;
    }
    a {
      text-decoration: underline;
    }
    a:visited {
      color: ${BBBright};
    }
    p {
      min-height: 14px;
    }

    /* 컨텐츠 컨테이너 */
    .site-content {
      margin: 0 auto !important;
      max-width: 1200px !important;
    }

    /* 인용문 */
    .wp-block-quote {
      margin: 16px 0;
      padding: 8px;
      border-radius: 4px;
      border-left: 4px solid #00428e;
      background: #f2f6f9;
      min-height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .wp-block-quote > p {
      display: none;
    }
    .wp-block-quote > cite {
      font-style: normal;
      font-size: 14px;
    }

    .wp-block-image img {
      height: auto;
      max-width: 1000px;
    }
    .alignwide img {
      width: 100%;
    }

    .wp-block-image {
      width: 100%;
    }

    .wp-block-video {
      padding: 16px 0;
    }
    .wp-block-video video {
      height: auto;
      max-width: 1000px;
      width: 100%;
    }
    .wp-block-embed {
      padding: 16px 0;
      text-align: center;
      height: auto;
      width: 100%;
      max-width: 1000px;
    }
    .wp-block-embed__wrapper {
      position: relative;
      height: auto;
    }
    .wp-block-embed__wrapper::before {
      content: '';
      display: block;
    }
    .embed-youtube .youtube-player {
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
    }
    .embed-videopress {
      width: 100% !important;
      height: auto !important;
    }

    .wp-embed-aspect-16-9 .wp-block-embed__wrapper::before {
      padding-top: 56.25%;
    }
    .wp-embed-aspect-4-3 .wp-block-embed__wrapper::before {
      padding-top: 75%;
    }
    .wp-embed-aspect-1-1 .wp-block-embed__wrapper::before {
      padding-top: 100%;
    }

    figure {
      clear: both;
      text-align: center;
      margin: 16px 0;
      max-width: 100%;
      display: table;
      margin-left: auto;
      margin-right: auto;
    }

    figure.is-provider-videopress .wp-block-embed__wrapper::before {
      display: none;
    }

    .is-provider-videopress div.padding-wrapper {
      padding-top: 56.25%;
    }

    figure.is-provider-videopress iframe {
      height: 100%;
      width: 100%;
      position: absolute;
      top: 0;
      left: 0;
    }

    /* 이미지 캡션 */
    figcaption.wp-element-caption,
    figcaption {
      color: #808080;
      font-size: 13px;
      margin: 0 auto;
      margin-top: 8px;
      line-height: 23px;
      letter-spacing: -0.01em;
      display: table-caption;
      caption-side: bottom;
      max-width: 1000px;
    }

    /* 일반 텍스트, 목록 */
    p,
    li {
      color: #000;
      font-weight: 400;
      font-size: 15px;
      line-height: 180%;
    }

    .wp-block-button a.wp-block-button__link {
      display: inline-block;
      margin: 0;
      color: #fff;
      background-color: #32373c;
      border-radius: 9999px;
      box-shadow: none;
      text-decoration: none;
      padding: calc(0.667em + 2px) calc(1.333em + 2px);
      font-size: 16px;
      transition: background-color 0.2s;
    }

    .wp-block-button a.wp-block-button__link:hover {
      background-color: #1f2225;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    p,
    ul,
    li,
    a,
    .wp-block-buttons,
    .wp-block-quote {
      max-width: 768px;
      margin: 0 auto;
    }
    .wp-block-buttons {
      padding: 8px 0;
      text-align: center;
    }
    .has-text-align-center {
      text-align: center;
    }

    p + p {
      margin-top: 16px;
    }
    strong {
      font-size: inherit;
      font-weight: bold;
    }
  }
  @media screen and (max-width: 1239px) {
    .content-area {
      padding: 16px 0;
    }
  }
  @media screen and (max-width: 599px) {
    figcaption.wp-element-caption {
      margin-top: 8px;
    }
    .related-post-list {
      justify-content: flex-start;
      overflow-x: auto;
    }
    .related-post-list > li + li {
      margin-left: 8px;
    }
    .tags-area {
      justify-content: center;
    }
    .content-area {
      padding: 8px 0;
    }
    .aligncenter img {
      width: 100%;
    }
  }
`;

const Tag = styled.li`
  color: ${SB};
  color: ${BBDARK};
  font-size: 15px;
  font-weight: 500;
  padding: 6px 20px;
  border: 1px solid ${GRAY80};
  border-radius: 40px;
  flex-shrink: 0;
  margin-top: 4px;
  margin-right: 8px;
  display: flex;
  align-items: center;
  font-family: 'Noto Sans KR', sans-serif;
`;
